import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Popover } from 'antd';
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  HolderOutlined,
  LoginOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import { useModel } from '@umijs/max';
import dayjs from 'dayjs';
import { dtFormat, parseDt } from '@/util';
import styles from './index.less';

// clockedInAt is a naive server-timezone string ("2026-08-11 11:53:55", no
// offset) — new Date(...) would parse it as if it were already in the
// browser's own local timezone, which is wrong whenever the two differ.
// parseDt attaches the server's timezone first, same as dtFormat elsewhere.
const formatElapsed = (clockedInAt: string): string => {
  const seconds = Math.max(0, dayjs().diff(parseDt(clockedInAt), 'second'));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

/**
 * Nav-bar clock in/out icon with hover popover — self-service, visible to CSRs
 * and to users with clockin.required. For the latter, the reminder and
 * auto-close modals are skipped: ClockInGate locks the whole app instead.
 */
const CsrClockWidget: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const firstName = (initialState?.currentUser?.display_name || initialState?.currentUser?.user_name || '')
    .trim()
    .split(' ')[0];

  const {
    enabled,
    mustClockIn,
    loaded,
    clockedInAt,
    clockedInToday,
    autoCloseNotice: sharedAutoCloseNotice,
    busy,
    dismissingNotice,
    clockIn,
    clockOut,
    dismissAutoClose,
  } = useModel('clockGate');
  // A forced user sees the auto-close reason on the gate, not in a modal.
  const autoCloseNotice = mustClockIn ? null : sharedAutoCloseNotice;

  const [, forceTick] = useState(0);
  const reminderShown = useRef(false);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [modalOffset, setModalOffset] = useState({ x: 0, y: 0 });
  const dragState = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(
    null,
  );
  const [dragging, setDragging] = useState(false);

  const handleDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: modalOffset.x,
      originY: modalOffset.y,
    };
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragState.current) return;
      setModalOffset({
        x: dragState.current.originX + (e.clientX - dragState.current.startX),
        y: dragState.current.originY + (e.clientY - dragState.current.startY),
      });
    };
    const handleMouseUp = () => setDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  const handleClockIn = async () => {
    if (await clockIn()) {
      setReminderOpen(false);
      setPopoverOpen(false);
    }
  };

  // Gentle one-time reminder after the first status load. Auto-close notice
  // takes priority — don't nag someone to clock in while they still need to
  // see why their last session was force-closed.
  useEffect(() => {
    if (!enabled || !loaded || reminderShown.current) return;
    reminderShown.current = true;
    if (!mustClockIn && !clockedInAt && !clockedInToday && !sharedAutoCloseNotice) {
      setModalOffset({ x: 0, y: 0 });
      setReminderOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, loaded]);

  // Re-render once a minute so the elapsed label keeps up.
  useEffect(() => {
    if (!clockedInAt) return;
    const timer = window.setInterval(() => forceTick((n) => n + 1), 60000);
    return () => window.clearInterval(timer);
  }, [clockedInAt]);

  if (!enabled || !loaded) return null;

  const handleClockOut = async () => {
    if (await clockOut()) setPopoverOpen(false);
  };

  const handleDismissAutoClose = dismissAutoClose;

  const handleAutoCloseClockIn = async () => {
    await handleDismissAutoClose();
    await handleClockIn();
  };

  const reminderModal = (
    <Modal
      open={reminderOpen}
      onCancel={() => setReminderOpen(false)}
      footer={null}
      centered
      width={392}
      destroyOnHidden
      maskClosable={false}
      zIndex={1050}
      styles={{ body: { padding: '28px 24px 24px' } }}
      modalRender={(node) => (
        <div style={{ transform: `translate(${modalOffset.x}px, ${modalOffset.y}px)` }}>{node}</div>
      )}
      title={
        <div
          onMouseDown={handleDragStart}
          style={{
            cursor: dragging ? 'grabbing' : 'grab',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginRight: 24,
            userSelect: 'none',
          }}
        >
          <HolderOutlined style={{ color: '#bfbfbf' }} />
          <span>Clock-in reminder</span>
        </div>
      }
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#e6f4ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <SmileOutlined style={{ fontSize: 26, color: '#1677ff' }} />
        </div>
        {firstName && (
          <div style={{ fontSize: 14, fontWeight: 500, color: '#1677ff', marginBottom: 4 }}>
            Hi {firstName},
          </div>
        )}
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Ready to clock in?</div>
        <div style={{ color: '#8c8c8c', marginBottom: 24 }}>
          Looks like you haven&apos;t clocked in yet today.
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <Button onClick={() => setReminderOpen(false)}>Maybe later</Button>
          <Button type="primary" loading={busy} onClick={handleClockIn}>
            Clock In
          </Button>
        </div>
      </div>
    </Modal>
  );

  const autoCloseModal = (
    <Modal
      open={!!autoCloseNotice}
      onCancel={handleDismissAutoClose}
      footer={null}
      centered
      width={392}
      destroyOnHidden
      maskClosable={false}
      zIndex={1050}
      styles={{ body: { padding: '28px 24px 24px' } }}
      modalRender={(node) => (
        <div style={{ transform: `translate(${modalOffset.x}px, ${modalOffset.y}px)` }}>{node}</div>
      )}
      title={
        <div
          onMouseDown={handleDragStart}
          style={{
            cursor: dragging ? 'grabbing' : 'grab',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginRight: 24,
            userSelect: 'none',
          }}
        >
          <HolderOutlined style={{ color: '#bfbfbf' }} />
          <span>Clocked out automatically</span>
        </div>
      }
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#fff7e6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <ExclamationCircleOutlined style={{ fontSize: 26, color: '#fa8c16' }} />
        </div>
        {firstName && (
          <div style={{ fontSize: 14, fontWeight: 500, color: '#fa8c16', marginBottom: 4 }}>
            Hi {firstName},
          </div>
        )}
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
          You were clocked out automatically
        </div>
        {autoCloseNotice && (
          <div style={{ color: '#8c8c8c', marginBottom: 24 }}>
            Your session from {dtFormat(autoCloseNotice.clocked_in_at, 'h:mm A')} to{' '}
            {dtFormat(autoCloseNotice.clocked_out_at, 'h:mm A')} was closed because no activity was
            detected. Clock back in if you&apos;re still working.
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <Button loading={dismissingNotice} onClick={handleDismissAutoClose}>
            Got it
          </Button>
          <Button type="primary" loading={busy} onClick={handleAutoCloseClockIn}>
            Clock In
          </Button>
        </div>
      </div>
    </Modal>
  );

  const popoverContent = (
    <div style={{ width: 220 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: clockedInAt ? '#f6ffed' : '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: clockedInAt ? '#389e0d' : '#8c8c8c',
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {clockedInAt ? <ClockCircleOutlined /> : <LoginOutlined />}
        </div>
        <div style={{ lineHeight: 1.4 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'rgba(0, 0, 0, 0.88)' }}>
            {clockedInAt ? 'Clocked in' : 'Clocked out'}
          </div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            {clockedInAt ? `Since ${dtFormat(clockedInAt, 'h:mm A')}` : "You're not on the clock"}
          </div>
        </div>
      </div>
      {clockedInAt && (
        <div
          style={{
            textAlign: 'center',
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: 0.3,
            color: '#389e0d',
            background: '#f6ffed',
            borderRadius: 8,
            padding: '10px 0',
            marginBottom: 14,
          }}
        >
          {formatElapsed(clockedInAt)}
        </div>
      )}
      <Button
        block
        danger={!!clockedInAt}
        type={clockedInAt ? 'default' : 'primary'}
        loading={busy}
        onClick={clockedInAt ? handleClockOut : handleClockIn}
      >
        {clockedInAt ? 'Clock Out' : 'Clock In'}
      </Button>
    </div>
  );

  return (
    <>
      <Popover
        content={popoverContent}
        trigger={['hover', 'click']}
        mouseLeaveDelay={0.2}
        open={!reminderOpen && !autoCloseNotice && popoverOpen}
        onOpenChange={(next) => {
          if (!reminderOpen && !autoCloseNotice) setPopoverOpen(next);
        }}
        placement="bottomRight"
        styles={{ body: { padding: 16, borderRadius: 12 } }}
      >
        <span className={styles.action} role="button" aria-label={clockedInAt ? 'Clocked in' : 'Clock in'}>
          {clockedInAt ? (
            <ClockCircleOutlined style={{ fontSize: 18, color: '#95de64' }} />
          ) : (
            <LoginOutlined style={{ fontSize: 18, color: '#fff' }} />
          )}
        </span>
      </Popover>
      {reminderModal}
      {autoCloseModal}
    </>
  );
};

export default CsrClockWidget;
